/**
 * Stock rastreado na coluna `products.estoque`.
 * `null`/vazio = sem controlo de quantidade nesta loja.
 *
 * @param {Record<string, unknown>|null|undefined} p
 * @returns {number|null}
 */
export function productTrackedStock(p) {
  if (!p || p.estoque == null || p.estoque === '') return null;
  const n = Number(p.estoque);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/** @param {Record<string, unknown>|null|undefined} p */
export function productIsSoldOut(p) {
  const tracked = productTrackedStock(p);
  return tracked !== null && tracked <= 0;
}

/** A partir deste valor (inclusive), a vitrine mostra aviso de stock baixo. */
export const LOW_STOCK_VITRINE_THRESHOLD = 10;

/**
 * Quantidade em stock para o badge da vitrine (1…threshold) ou null.
 *
 * @param {Record<string, unknown>|null|undefined} p
 * @param {number} [threshold]
 * @returns {number|null}
 */
export function productLowStockVitrineCount(p, threshold = LOW_STOCK_VITRINE_THRESHOLD) {
  const tracked = productTrackedStock(p);
  const max = Math.max(1, Math.floor(Number(threshold) || LOW_STOCK_VITRINE_THRESHOLD));
  if (tracked === null || tracked <= 0 || tracked > max) return null;
  return tracked;
}

/**
 * @param {Record<string, unknown>|null|undefined} p
 * @param {number} [threshold]
 * @returns {string|null}
 */
export function productLowStockVitrineLabel(p, threshold = LOW_STOCK_VITRINE_THRESHOLD) {
  const count = productLowStockVitrineCount(p, threshold);
  if (count === null) return null;
  if (count === 1) return 'Somente 1 unidade em estoque';
  return `Somente ${count} unidades em estoque`;
}

/**
 * @param {string} productName
 * @param {number} available
 * @param {number} requested
 */
export function formatStockLimitMessage(productName, available, requested) {
  const nome = String(productName || 'Produto').trim() || 'Produto';
  const av = Math.max(0, Math.floor(Number(available) || 0));
  const req = Math.max(0, Math.floor(Number(requested) || 0));
  if (av <= 0) return `${nome} está esgotado.`;
  if (req > av) return `${nome}: só há ${av} em stock (você pediu ${req}).`;
  return '';
}

/**
 * Texto do modal de stock limitado.
 *
 * @returns {{ title: string, body: string }}
 */
export function buildStockAlertText(productName, available) {
  const av = Math.max(0, Math.floor(Number(available) || 0));
  if (av <= 0) {
    return {
      title: 'Produto esgotado',
      body: 'Este produto está esgotado no momento.',
    };
  }
  if (av === 1) {
    return {
      title: 'Stock limitado',
      body: 'Este produto contém 1 unidade em estoque.',
    };
  }
  return {
    title: 'Stock limitado',
    body: `Este produto contém apenas ${av} unidades em estoque.`,
  };
}

/**
 * Ajusta quantidades da sacola ao stock disponível.
 *
 * @param {Record<string, number>} cartObj
 * @param {Array<Record<string, unknown>>} productsList
 * @returns {{ cart: Record<string, number>, message: string, changed: boolean }}
 */
export function clampCartToStock(cartObj, productsList) {
  /** @type {Record<number, Record<string, unknown>>} */
  const byId = {};
  for (const p of productsList) {
    const id = Number(p?.id);
    if (Number.isFinite(id) && id > 0) byId[id] = p;
  }

  /** @type {Record<string, number>} */
  const next = {};
  let message = '';
  let changed = false;

  for (const [pid, rawQty] of Object.entries(cartObj || {})) {
    const q0 = Math.max(0, Math.floor(Number(rawQty) || 0));
    if (q0 <= 0) {
      if (rawQty != null && q0 !== rawQty) changed = true;
      continue;
    }
    const p = byId[Number(pid)];
    const tracked = productTrackedStock(p);
    let q = q0;
    if (tracked !== null && q > tracked) {
      message = formatStockLimitMessage(String(p?.nome || ''), tracked, q);
      q = tracked;
      changed = true;
    }
    if (tracked !== null && tracked <= 0) {
      message = formatStockLimitMessage(String(p?.nome || ''), 0, q0);
      changed = true;
      continue;
    }
    if (q <= 0) {
      changed = true;
      continue;
    }
    next[pid] = q;
    if (q !== q0) changed = true;
  }

  if (!changed) {
    for (const pid of Object.keys(cartObj || {})) {
      if (!(pid in next)) changed = true;
    }
  }

  return { cart: next, message, changed };
}
